import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  FaArrowLeft, 
  FaCalendarAlt, 
  FaUser, 
  FaTag, 
  FaSearch,
  FaShieldAlt,
  FaCode,
  FaLightbulb,
  FaRocket,
  FaBug,
  FaEye,
  FaCog,
  FaArrowRight,
  FaClock,
  FaFire,
  FaPaperPlane,
  FaTimes,
  FaChevronRight
} from "react-icons/fa";
import Logo from "../components/Logo";
import Footer from "../components/Footer";
import "../styles/Blog.css";

// Sample blog posts data
const blogPosts = [
    {
    id: 1,
    title: "Revolutionizing Malware Analysis: How triagz empowers security researchers with AI-driven investigation",
    excerpt: "Discover how Triagz revolutionizes malware analysis with natural language queries. From PE file analysis and TTP extraction to cross-platform threat hunting, learn how security researchers can leverage AI-powered analysis for faster, more comprehensive threat detection.",
    content: "Full blog post content would go here...",
    author: "Security Research Team",
    date: "2024-01-15",
    category: "Malware Analysis",
    tags: ["AI", "Malware", "Analysis", "DeepSeek", "TTP", "YARA", "Cross-Platform"],
    readTime: "12 min read",
    featured: false,
    image: "/api/placeholder/800/400"
  },
  {
    id: 2,
    title: "Cross-Platform Endpoint Security: Windows, Linux, and macOS Best Practices",
    excerpt: "Learn the essential techniques for securing endpoints across different operating systems and how triagz's unified approach simplifies multi-platform security research.",
    content: "Full blog post content would go here...",
    author: "DevOps Team",
    date: "2024-01-10",
    category: "Security",
    tags: ["Cross-Platform", "Endpoint", "Security", "Best Practices"],
    readTime: "6 min read",
    featured: false,
    image: "/api/placeholder/800/400"
  },
  {
    id: 3,
    title: "Building custom YARA rules for enhanced threat detection",
    excerpt: "Master the art of creating effective YARA rules that can detect sophisticated threats and integrate seamlessly with triagz's analysis engine.",
    content: "Full blog post content would go here...",
    author: "Threat Intelligence Team",
    date: "2024-01-08",
    category: "Threat Detection",
    tags: ["YARA", "Rules", "Threat Detection", "Custom"],
    readTime: "10 min read",
    featured: false,
    image: "/api/placeholder/800/400"
  },
  {
    id: 4,
    title: "API Integration Guide: extending triagz with custom tools",
    excerpt: "Discover how to integrate your existing security tools with triagz's robust API ecosystem for a comprehensive security research workflow.",
    content: "Full blog post content would go here...",
    author: "Development Team",
    date: "2024-01-05",
    category: "Development",
    tags: ["API", "Integration", "Tools", "Workflow"],
    readTime: "12 min read",
    featured: false,
    image: "/api/placeholder/800/400"
  },
  {
    id: 5,
    title: "Incident Response Automation: From detection to remediation",
    excerpt: "Learn how to automate your incident response workflow using triagz's AI-powered analysis capabilities and real-time collaboration features.",
    content: "Full blog post content would go here...",
    author: "Incident Response Team",
    date: "2024-01-03",
    category: "Incident Response",
    tags: ["Automation", "Incident Response", "AI", "Collaboration"],
    readTime: "7 min read",
    featured: false,
    image: "/api/placeholder/800/400"
  },
  {
    id: 6,
    title: "Open Source Security: The triagz Philosophy",
    excerpt: "Understand why transparency and community collaboration are at the heart of effective cybersecurity research and how triagz embodies these principles.",
    content: "Full blog post content would go here...",
    author: "Founders",
    date: "2024-01-01",
    category: "Philosophy",
    tags: ["Open Source", "Community", "Philosophy", "Security"],
    readTime: "5 min read",
    featured: false,
    image: "/api/placeholder/800/400"
  },
  {
    id: 7,
    title: "Complete Installation Guide: Setting up Triagz Agents on Windows, Linux, and macOS",
    excerpt: "Step-by-step instructions for installing and configuring Triagz agents across all three major operating systems. From Windows MSI packages to Linux binaries and macOS applications, get your endpoints connected quickly and securely.",
    content: "Full blog post content would go here...",
    author: "Triagz Development Team",
    date: "2024-02-01",
    category: "Installation",
    tags: ["Installation", "Windows", "Linux", "macOS", "Setup", "Agent", "Cross-Platform"],
    readTime: "15 min read",
    featured: true,
    image: "/api/placeholder/800/400"
  }
];

const categories = ["All", "Malware Analysis", "Security", "Threat Detection", "Development", "Incident Response", "Philosophy", "Installation"];

function Blog() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Handle scroll for header
  useEffect(() => {
    const handleScroll = () => {
      setIsHeaderScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const featuredPost = blogPosts.find(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  const getCategoryIcon = (category) => {
    switch (category) {
      case "Malware Analysis": return <FaBug />;
      case "Security": return <FaShieldAlt />;
      case "Threat Detection": return <FaEye />;
      case "Development": return <FaCode />;
      case "Incident Response": return <FaRocket />;
      case "Philosophy": return <FaLightbulb />;
      case "Installation": return <FaCog />;
      default: return <FaTag />;
    }
  };

  const clearFilters = () => {
    setSelectedCategory("All");
    setSearchTerm("");
  };

  return (
    <div className="blog-container">
      {/* Ambient Background */}
      <div className="blog-ambient-bg">
        <div className="ambient-orb ambient-orb-1"></div>
        <div className="ambient-orb ambient-orb-2"></div>
      </div>

      {/* Header */}
      <header className={`blog-header ${isHeaderScrolled ? 'scrolled' : ''}`}>
        <div className="header-content">
          <Link to="/" className="header-logo">
            <Logo size="small" showText={false} />
          </Link>
          <nav className="header-nav">
            <Link to="/" className="nav-link">Home</Link>
            <Link to="/blog" className="nav-link active">Blog</Link>
            <Link to="/downloads" className="nav-link">Downloads</Link>
          </nav>
          <div className="header-actions">
            <Link to="/login" className="login-btn">Login</Link>
            <Link to="/register" className="register-btn">Get Started</Link>
          </div>
        </div>
      </header>

      <main className="blog-main">
        {/* Hero Section */}
        <section className="blog-hero">
          <div className="hero-decoration">
            <div className="deco-grid"></div>
          </div>
          <div className="container">
            <Link to="/" className="back-link">
              <FaArrowLeft />
              <span>Back to Home</span>
            </Link>
            <div className="hero-content">
              <h1>Security Research Blog</h1>
              <p>
                Stay updated with the latest insights, tutorials, and research from the triagz team. 
                Discover advanced security techniques, AI-powered analysis methods, and industry best practices.
              </p>
            </div>
          </div>
        </section>

        {/* Search and Filter Section */}
        <section className="blog-controls">
          <div className="container">
            <div className="controls-wrapper">
              <div className={`search-container ${isSearchFocused ? 'focused' : ''}`}>
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search articles, topics, or tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                />
                {searchTerm && (
                  <button className="clear-search" onClick={() => setSearchTerm("")}>
                    <FaTimes />
                  </button>
                )}
              </div>
              <div className="category-filters">
                {categories.map(category => (
                  <button
                    key={category}
                    className={`filter-chip ${selectedCategory === category ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category !== "All" && getCategoryIcon(category)}
                    <span>{category}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Featured Post */}
        {featuredPost && selectedCategory === "All" && searchTerm === "" && (
          <section className="featured-section">
            <div className="container">
              <div className="section-header">
                <div className="section-label">
                  <FaFire />
                  <span>Featured Article</span>
                </div>
              </div>
              <article className="featured-card">
                <div className="featured-glow"></div>
                <div className="featured-content">
                  <div className="featured-meta">
                    <span className="category-badge">
                      {getCategoryIcon(featuredPost.category)}
                      {featuredPost.category}
                    </span>
                    <span className="featured-badge">
                      <FaFire />
                      Featured
                    </span>
                  </div>
                  <h2>{featuredPost.title}</h2>
                  <p>{featuredPost.excerpt}</p>
                  <div className="featured-footer">
                    <div className="author-info">
                      <div className="author-avatar">
                        <FaUser />
                      </div>
                      <div className="author-details">
                        <span className="author-name">{featuredPost.author}</span>
                        <div className="post-meta-inline">
                          <span>
                            <FaCalendarAlt />
                            {new Date(featuredPost.date).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </span>
                          <span>
                            <FaClock />
                            {featuredPost.readTime}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Link to={`/blog/${featuredPost.id}`} className="read-article-btn">
                      Read Article
                      <FaArrowRight />
                    </Link>
                  </div>
                </div>
                <div className="featured-visual">
                  <div className="visual-pattern">
                    <div className="pattern-circle pattern-circle-1"></div>
                    <div className="pattern-circle pattern-circle-2"></div>
                    <div className="pattern-circle pattern-circle-3"></div>
                    <div className="pattern-icon">
                      {getCategoryIcon(featuredPost.category)}
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </section>
        )}

        {/* Blog Posts Grid */}
        <section className="posts-section">
          <div className="container">
            <div className="section-header">
              <div className="section-title">
                <h2>
                  {selectedCategory === "All" ? "Latest Articles" : `${selectedCategory}`}
                </h2>
                <span className="posts-count">{regularPosts.length} articles</span>
              </div>
              {(selectedCategory !== "All" || searchTerm !== "") && (
                <button className="clear-filters-btn" onClick={clearFilters}>
                  <FaTimes />
                  Clear Filters
                </button>
              )}
            </div>
            
            {regularPosts.length === 0 ? (
              <div className="no-posts">
                <div className="no-posts-icon">
                  <FaSearch />
                </div>
                <h3>No articles found</h3>
                <p>Try adjusting your search or filter criteria</p>
                <button className="reset-btn" onClick={clearFilters}>
                  Reset Filters
                </button>
              </div>
            ) : (
              <div className="posts-grid">
                {regularPosts.map((post, index) => (
                  <article 
                    key={post.id} 
                    className={`post-card ${index === 0 ? 'post-card-large' : ''}`}
                  >
                    <div className="card-glow"></div>
                    <div className="card-content">
                      <div className="card-header">
                        <span className="category-tag">
                          {getCategoryIcon(post.category)}
                          {post.category}
                        </span>
                        <span className="read-time">
                          <FaClock />
                          {post.readTime}
                        </span>
                      </div>
                      <h3>{post.title}</h3>
                      <p>{post.excerpt}</p>
                      <div className="card-tags">
                        {post.tags.slice(0, 3).map(tag => (
                          <span key={tag} className="tag">#{tag}</span>
                        ))}
                        {post.tags.length > 3 && (
                          <span className="tag tag-more">+{post.tags.length - 3}</span>
                        )}
                      </div>
                    </div>
                    <div className="card-footer">
                      <div className="author-compact">
                        <FaUser />
                        <span>{post.author}</span>
                        <span className="separator">•</span>
                        <span>
                          {new Date(post.date).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </span>
                      </div>
                      <Link to={`/blog/${post.id}`} className="read-more-link">
                        Read More
                        <FaChevronRight />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="newsletter-section">
          <div className="container">
            <div className="newsletter-card">
              <div className="newsletter-glow"></div>
              <div className="newsletter-content">
                <div className="newsletter-icon">
                  <FaPaperPlane />
                </div>
                <h2>Stay Updated</h2>
                <p>Get the latest security research insights and triagz updates delivered to your inbox.</p>
                <form className="newsletter-form" onSubmit={(e) => e.preventDefault()}>
                  <div className="form-group">
                    <input 
                      type="email" 
                      placeholder="Enter your email address"
                      required
                    />
                    <button type="submit" className="subscribe-btn">
                      Subscribe
                      <FaArrowRight />
                    </button>
                  </div>
                </form>
                <p className="privacy-note">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </div>
              <div className="newsletter-decoration">
                <div className="decoration-ring decoration-ring-1"></div>
                <div className="decoration-ring decoration-ring-2"></div>
                <div className="decoration-ring decoration-ring-3"></div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default Blog;
